import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined';
import SimpleDialog from 'crqlar-react-common/dist/components/SimpleDialog';
import {Service} from 'crqlar-react-common/dist/models/service/service.model';
import {useServicesForSector} from 'crqlar-react-common/dist/utils/hooks/services/useServices';
import {useAllSectors} from 'crqlar-react-common/dist/utils/hooks/sectors/useSectors';
import {SectorType} from 'crqlar-react-common/dist/models/sector/sector.model';
import ServiceEditDialog from '../../../../system/services/ServiceEditDialog';
import {useFloorplanContext} from '../hooks/useFloorplanContext';
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const SERVICE_ACTIONS_WIDTH = 70;
const LIST_ITEM_RIGHT_PADDING = 16;

interface Props {
    services: Service[];
    isLoading?: boolean;
    onRemoveService: (serviceId: string) => void;
    onSelectedServicesChange: (services: Service[]) => void;
}

const EditServiceServices: React.FC<Props> = ({
    services,
    isLoading = false,
    onRemoveService,
    onSelectedServicesChange,
}) => {
    const {t} = useTranslation();
    const {restaurantId, refetchRestaurantServices} = useFloorplanContext();
    const sectors = useAllSectors();
    const restaurantSectorId = sectors.find((sector) => sector.sectorType === SectorType.RESTAURANT)?.id;
    const restaurantSectorServices = useServicesForSector(restaurantSectorId);
    const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
    const [servicePendingRemoval, setServicePendingRemoval] = useState<Service | null>(null);
    const [serviceActionsMenu, setServiceActionsMenu] = useState<{
        anchorEl: HTMLButtonElement;
        service: Service;
    } | null>(null);
    const [addExistingAnchorEl, setAddExistingAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpenAddExisting = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAddExistingAnchorEl(event.currentTarget);
    };

    const handleCloseAddExisting = () => {
        setAddExistingAnchorEl(null);
    };

    const handleCreateNew = () => {
        setEditingServiceId('new');
    };

    const handleEdit = (id: string) => {
        setEditingServiceId(id);
    };

    const handleOpenServiceActions = (event: React.MouseEvent<HTMLButtonElement>, service: Service) => {
        event.stopPropagation();
        setServiceActionsMenu({anchorEl: event.currentTarget, service});
    };

    const handleCloseServiceActions = () => {
        setServiceActionsMenu(null);
    };

    const handleDeleteService = () => {
        if (!serviceActionsMenu) return;

        setServicePendingRemoval(serviceActionsMenu.service);
        handleCloseServiceActions();
    };

    const handleCloseDialog = () => {
        setEditingServiceId(null);
        void refetchRestaurantServices();
    };

    const formatServiceMeta = (durationMinutes?: number) => {
        if (!durationMinutes) return undefined;
        return t('{{count}} min', {count: durationMinutes});
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{p: 2, pb: 1, display: "flex", flexDirection:"row", gap: 1}}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon/>}
                    onClick={handleOpenAddExisting}
                    fullWidth
                >
                    {t('Add existing')}
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<AddIcon/>}
                    onClick={handleCreateNew}
                    fullWidth
                >
                    {t('Create new')}
                </Button>
            </Box>

            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                background: `
                    linear-gradient(#fff 30%, transparent) center top / 100% 40px,
                    radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.15), transparent) center top / 100% 6px
                `,
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'local, scroll',
                bgcolor: '#fff',
            }}>
                {isLoading && (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                        <CircularProgress size={24}/>
                    </Box>
                )}

                {!isLoading && (
                    <Typography variant="body1" sx={{px: 2, pt: 1.5, pb: 0.5}}>
                        {t('Services')} ({services.length})
                    </Typography>
                )}

                {services.length > 0 && (
                    <List dense disablePadding>
                        {services.map((service) => (
                            <ListItem
                                key={service.id}
                                disablePadding
                                secondaryAction={
                                <Stack direction={"row"} sx={{width: `${SERVICE_ACTIONS_WIDTH}px`, justifyContent: 'flex-end', gap: 1}}>
                                    <Tooltip title={t('Edit')} placement="top" disableInteractive
                                             slotProps={{popper: {modifiers: [{name: 'offset', options: {offset: [0, -8]}}]}}}>
                                        <IconButton size="small" onClick={() => handleEdit(service.id!)}>
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('More')} placement="top" disableInteractive
                                             slotProps={{popper: {modifiers: [{name: 'offset', options: {offset: [0, -8]}}]}}}>
                                        <IconButton
                                            id={`floorplan-service-actions-${service.id}`}
                                            size="small"
                                            aria-label={t('More actions for {{name}}', {name: service.name ?? t('Service')})}
                                            aria-haspopup="menu"
                                            aria-controls={serviceActionsMenu?.service.id === service.id
                                                ? 'floorplan-service-actions-menu'
                                                : undefined}
                                            aria-expanded={serviceActionsMenu?.service.id === service.id || undefined}
                                            onClick={(event) => handleOpenServiceActions(event, service)}
                                        >
                                            <MoreVertIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                }
                            >
                                <ListItemButton
                                    sx={{
                                        pr: `${SERVICE_ACTIONS_WIDTH + LIST_ITEM_RIGHT_PADDING}px`,
                                        minHeight: 44,
                                        minWidth: 0,
                                    }}
                                >
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <RoomServiceOutlinedIcon fontSize="small"/>
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={{minWidth: 0}}
                                        primary={service.name ?? t('Service')}
                                        secondary={formatServiceMeta(service.durationMinutes)}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontSize: '0.875rem',
                                            noWrap: true,
                                            title: service.name ?? t('Service'),
                                        }}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                            color: 'text.secondary',
                                            noWrap: true,
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}

                {!isLoading && services.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{px: 2, py: 1}}>
                        {t('No services found')}
                    </Typography>
                )}
            </Box>

            <Menu
                id="floorplan-service-actions-menu"
                anchorEl={serviceActionsMenu?.anchorEl}
                open={!!serviceActionsMenu}
                onClose={handleCloseServiceActions}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                MenuListProps={{
                    'aria-labelledby': serviceActionsMenu
                        ? `floorplan-service-actions-${serviceActionsMenu.service.id}`
                        : undefined,
                }}
            >
                <MenuItem onClick={handleDeleteService} sx={{color: 'error.main'}}>
                    <ListItemIcon sx={{color: 'inherit'}}>
                        <DeleteIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText primary={t('Delete')}/>
                </MenuItem>
            </Menu>

            {editingServiceId && (
                <ServiceEditDialog
                    serviceId={editingServiceId}
                    open={!!editingServiceId}
                    onClose={handleCloseDialog}
                    defaultResourceId={restaurantId}
                />
            )}

            <SimpleDialog
                open={!!servicePendingRemoval}
                onClose={() => setServicePendingRemoval(null)}
                title={t('Remove service?')}
                message={t('This only removes the service from the current floor plan draft. The change is saved when you save the floor plan.')}
                confirmText={t('Remove')}
                cancelText={t('Cancel')}
                onConfirm={() => {
                    if (servicePendingRemoval?.id) {
                        onRemoveService(servicePendingRemoval.id);
                    }
                    setServicePendingRemoval(null);
                }}
            />

            <Popover
                open={!!addExistingAnchorEl}
                anchorEl={addExistingAnchorEl}
                onClose={handleCloseAddExisting}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                PaperProps={{sx: {width: 420, maxWidth: 'calc(100vw - 32px)', p: 2}}}
            >
                <Stack spacing={1.5}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('Add existing services')}
                    </Typography>
                    <Autocomplete<Service, true>
                        id={'floorplan-existing-services'}
                        multiple
                        options={restaurantSectorServices}
                        getOptionLabel={(option) => option.name || ''}
                        value={services}
                        onChange={(_event, value) => {
                            onSelectedServicesChange(
                                value.filter((service): service is Service => !!service.id),
                            );
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size="small"
                                placeholder={t('Select services')}
                            />
                        )}
                        fullWidth
                    />
                    <Alert severity="info">
                        {t('Service assignment changes are only saved when you save the floor plan.')}
                    </Alert>
                </Stack>
            </Popover>
        </Box>
    );
};

export default EditServiceServices;
